/* eslint-disable @stencil-community/strict-mutable */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  getAssetPath,
  Method
} from "@stencil/core";
import { getIconPath } from "@genexus/mercury";

import { Locale } from "../../../common/locale";

const HEADER_ILLUSTRATION = getAssetPath(
  `./gx-ide-assets/chat-welcome/images/header-illustration.svg`
);
const AI_AVATAR = getAssetPath(
  `./gx-ide-assets/chat-welcome/images/ai-avatar-welcome.svg`
);

const HEADER_TRANSITION_DELAY: number = 100;
const HEADER_TRANSITION_DURATION: number = 500;
const SUGGESTED_PROMPT_BUTTON_INTERVAL: number = 200;
const SUGGESTED_PROMPT_BUTTONS_DELAY: number = 500;
const WELCOME_EXIT_DURATION: number = 100;

const AI_ICON = getIconPath({
  category: "system",
  name: "assistant",
  colorType: "primary"
});
const ARROW_RIGHT_ICON = getIconPath({
  category: "system",
  name: "arrow-right",
  colorType: "primary"
});

const CSS_BUNDLES = [
  "resets/box-sizing",
  "components/icon",
  "utils/typography"
];

@Component({
  tag: "gx-ide-chat-welcome",
  styleUrl: "chat-welcome.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/chat-welcome"]
})
export class GxIdeChatWelcome {
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  @Element() el: HTMLGxIdeChatWelcomeElement;

  /**
   * When true, it will display the welcome elements (title, greeting, and buttons)
   */
  @State() showHeader: boolean = false;

  /**
   * When true, it will display the suggested prompts buttons
   */
  @State() showSuggestedPrompts: boolean = false;

  /**
   * When true, it will hide entire welcome layout
   */
  @State() hideWelcome: boolean = false;

  /**
   * The welcome title, if not provided, the default title will be used.
   */
  @Prop({ mutable: true }) welcomeTitle?: string;

  /**
   * The welcome greeting description, if not provided, the default title will be used.
   */
  @Prop({ mutable: true }) welcomeGreeting?: string;

  /**
   * The suggested prompts, if not provided, the default title will be used.
   */
  @Prop({ mutable: true }) suggestedPrompts?: string[];

  /**
   * Fired when the user clicks on a suggested prompt button
   */
  @Prop() readonly suggestedPrompRequestedCallback: (
    prompt: string
  ) => Promise<void>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);

    // Set defaults
    if (!this.welcomeTitle) {
      this.welcomeTitle = this.#componentLocale.welcomeTitle;
    }
    if (!this.welcomeGreeting) {
      this.welcomeGreeting = this.#componentLocale.welcomeGreeting;
    }
    if (!this.suggestedPrompts) {
      this.suggestedPrompts = this.#componentLocale.suggestedPrompts;
    }
  }

  componentDidRender() {
    if (!this.hideWelcome) {
      this.#animationEntrance();
    }
  }

  /**
   * It will exit the wellcome screen and remove it from the dom.
   */
  @Method()
  async exitWelcome() {
    this.#animationExit();
  }

  #animationEntrance = () => {
    setTimeout(() => {
      this.showHeader = true;
      setTimeout(() => {
        this.showSuggestedPrompts = true;
      }, SUGGESTED_PROMPT_BUTTONS_DELAY);
    }, HEADER_TRANSITION_DELAY + HEADER_TRANSITION_DURATION);
  };

  #sendPromptToChat = async (e: MouseEvent) => {
    const prompt = (e.currentTarget as HTMLButtonElement).dataset["prommpt"];
    await this.#animationExit();
    this.suggestedPrompRequestedCallback(prompt);
  };

  #animationExit = async (): Promise<void> => {
    this.hideWelcome = true;
    await new Promise(resolve => setTimeout(resolve, WELCOME_EXIT_DURATION));
  };

  render() {
    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <div
          class={{ "welcome-container": true, "hidden": this.hideWelcome }}
          style={{
            "backgroundImage": `url(${HEADER_ILLUSTRATION})`,
            "--transition-duration": `${WELCOME_EXIT_DURATION}ms`
          }}
        >
          <header
            class={{ "header": true, "header--visible": this.showHeader }}
            style={{
              "--transition-delay": `${HEADER_TRANSITION_DELAY}ms`,
              "--transition-duration": `${HEADER_TRANSITION_DURATION}ms`
            }}
          >
            <span
              class="header__circle"
              style={{ backgroundImage: `url(${AI_AVATAR})` }}
            ></span>
            <h2 class="header__title">{this.welcomeTitle}</h2>
            <p class="header__greeting">{this.welcomeGreeting}</p>
          </header>

          {this.suggestedPrompts && this.showSuggestedPrompts && (
            <ul class="suggested-prompts">
              {this.suggestedPrompts.map((prompt, i) => (
                <li class="suggested-prompts__item">
                  <button
                    class={{
                      "suggested-prompts__button": true
                    }}
                    onClick={this.#sendPromptToChat}
                    style={{
                      "--delay": `${SUGGESTED_PROMPT_BUTTON_INTERVAL * i}ms`
                    }}
                    data-prommpt={prompt}
                  >
                    <ch-image
                      src={AI_ICON}
                      class="icon-m suggested-prompts__icon"
                    ></ch-image>
                    {prompt}
                    <ch-image
                      src={ARROW_RIGHT_ICON}
                      class="icon-m suggested-prompts__icon"
                    ></ch-image>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Host>
    );
  }
}
